import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PhotoUpload from '../components/PhotoUpload';

export default function ProductDetail() {
  const { productId } = useParams();
  const { currentUser } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [currentPrice, setCurrentPrice] = useState(0);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartXRef = useRef(null);
  const touchEndXRef = useRef(null);
  const autoPlayRef = useRef(null);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    calculatePrice();
  }, [selectedOptions, product]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'products', productId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const productData = { id: docSnap.id, ...docSnap.data() };
        setProduct(productData);
        setCurrentPrice(productData.basePrice);
        
        // Initialize selected options with first choice for each option
        const initialOptions = {};
        if (productData.options) {
          productData.options.forEach((option, index) => {
            if (option.choices && option.choices.length > 0) {
              initialOptions[index] = 0; // Select first choice by default
            }
          });
        }
        setSelectedOptions(initialOptions);
      } else {
        navigate('/shop');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!product) return;
    
    let finalPrice = product.basePrice;
    
    // Add option costs
    Object.keys(selectedOptions).forEach(optionIndex => {
      const option = product.options[optionIndex];
      const choiceIndex = selectedOptions[optionIndex];
      if (option.prices && option.prices[choiceIndex]) {
        finalPrice += option.prices[choiceIndex];
      }
    });
    
    // Apply discount
    if (product.discount?.type === 'percentage' && product.discount?.value) {
      finalPrice = finalPrice * (1 - product.discount.value / 100);
    } else if (product.discount?.type === 'fixed' && product.discount?.value) {
      finalPrice = Math.max(0, finalPrice - product.discount.value);
    }
    
    setCurrentPrice(finalPrice);
  };

  const handleOptionChange = (optionIndex, choiceIndex) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionIndex]: choiceIndex
    }));
  };

  // Debug currently selected options
  useEffect(() => {
    if (product) {
      console.log('[ProductDetail] selectedOptions', selectedOptions);
    }
  }, [selectedOptions, product]);

  const handleShopNow = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    if (product.category === 'Photobox') {
      setShowPhotoUpload(true);
    } else {
      // For non-Photobox products, add to cart then proceed to order summary
      const item = {
        productId: product.id,
        title: product.title,
        options: selectedOptions,
        price: currentPrice,
        photos: []
      };
      try {
        const id = await addToCart(item);
        console.log('[ProductDetail] non-photobox addToCart id', id);
      } catch (err) {
        console.error('[ProductDetail] addToCart failed', err);
      }
      navigate('/order-summary', { 
        state: { 
          productId: product.id,
          selectedOptions,
          finalPrice: currentPrice
        }
      });
    }
  };

  // Auto advance images every 15 seconds
  useEffect(() => {
    if (!product?.images || product.images.length <= 1) return;
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    autoPlayRef.current = setInterval(() => {
      setCurrentImageIndex((idx) => (idx + 1) % product.images.length);
    }, 5000);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [product]);

  if (loading) {
    return <div className="loading">Loading product...</div>;
  }

  if (!product) {
    return <div className="error">Product not found</div>;
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <button onClick={() => navigate('/shop')} className="back-btn">
          ← Back to Shop
        </button>
        <h1>{product.title}</h1>
      </header>
      
      <main className="page-content">
        <div className="product-detail-container">
          {/* Product Images */}
          <div className="product-images">
            {product.images && product.images.length > 0 ? (
              <div className="image-gallery">
                <div
                  className="main-image-wrapper"
                  onTouchStart={(e) => { touchStartXRef.current = e.changedTouches[0].clientX; }}
                  onTouchMove={(e) => { touchEndXRef.current = e.changedTouches[0].clientX; }}
                  onTouchEnd={() => {
                    const start = touchStartXRef.current;
                    const end = touchEndXRef.current;
                    if (start == null || end == null) return;
                    const delta = end - start;
                    const threshold = 40; // swipe threshold in px
                    if (delta > threshold) {
                      // swipe right -> prev image
                      setCurrentImageIndex((idx) => (idx - 1 + product.images.length) % product.images.length);
                    } else if (delta < -threshold) {
                      // swipe left -> next image
                      setCurrentImageIndex((idx) => (idx + 1) % product.images.length);
                    }
                    touchStartXRef.current = null;
                    touchEndXRef.current = null;
                  }}
                >
                  <img 
                    src={product.images[currentImageIndex]} 
                    alt={product.title}
                    className="main-image"
                  />
                  {product.images.length > 1 && (
                    <>
                      <button className="img-nav left" onClick={() => setCurrentImageIndex((idx) => (idx - 1 + product.images.length) % product.images.length)}>‹</button>
                      <button className="img-nav right" onClick={() => setCurrentImageIndex((idx) => (idx + 1) % product.images.length)}>›</button>
                    </>
                  )}
                </div>
                {product.images.length > 1 && (
                  <div className="thumbnail-images">
                    {product.images.map((image, index) => (
                      <img 
                        key={index}
                        src={image} 
                        alt={`${product.title} ${index + 1}`}
                        className={`thumbnail ${currentImageIndex === index ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="no-image">No Image Available</div>
            )}
          </div>

          {/* Product Information */}
          <div className="product-info">
            <div className="product-header">
              <h2>{product.title}</h2>
              {product.category && (
                <span className="category-badge">{product.category}</span>
              )}
            </div>
            
            <p className="product-description">{product.description}</p>
            
            {/* Price Display */}
            <div className="price-section">
              <div className="current-price">
                <span className="price-label">Price:</span>
                <span className="price-amount">${currentPrice.toFixed(2)}</span>
              </div>
              
              {product.discount?.type !== 'none' && (
                <div className="discount-info">
                  <span className="original-price">${product.basePrice.toFixed(2)}</span>
                  <span className="discount-badge">
                    {product.discount.type === 'percentage' 
                      ? `${product.discount.value}% OFF` 
                      : `$${product.discount.value} OFF`
                    }
                  </span>
                </div>
              )}
            </div>

            {/* Product Options */}
            {product.options && product.options.length > 0 && (
              <div className="product-options">
                <h3>Customize Your Product</h3>
                {product.options.map((option, optionIndex) => (
                  <div key={optionIndex} className="option-group">
                    <label className="option-label">{option.name}</label>
                    <div className="option-choices">
                      {option.choices.map((choice, choiceIndex) => (
                        <label key={choiceIndex} className="choice-radio">
                          <input
                            type="radio"
                            name={`option-${optionIndex}`}
                            value={choiceIndex}
                            checked={selectedOptions[optionIndex] === choiceIndex}
                            onChange={() => handleOptionChange(optionIndex, choiceIndex)}
                          />
                          <span className="choice-text">
                            {choice}
                            {option.prices && option.prices[choiceIndex] > 0 && (
                              <span className="choice-price">
                                (+${option.prices[choiceIndex].toFixed(2)})
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="product-actions">
              {currentUser ? (
                <button 
                  onClick={handleShopNow}
                  className="shop-now-btn primary"
                >
                  {product.category === 'Photobox' ? 'Customize Now' : 'Add to Cart'}
                </button>
              ) : (
                <div className="login-required">
                  <p>Login required to purchase this product</p>
                  <button 
                    onClick={() => navigate('/login')}
                    className="login-btn"
                  >
                    Login / Register
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Photo Upload Modal for Photobox Products */}
        {showPhotoUpload && product.category === 'Photobox' && (
          <PhotoUpload
            product={product}
            selectedOptions={selectedOptions}
            finalPrice={currentPrice}
            onClose={() => setShowPhotoUpload(false)}
          />
        )}
      </main>
    </div>
  );
}
